import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { updateDocumentParsingProgress } from "@/hooks/documents/useDocumentParsingStatus";
import { processingDocumentsStorage } from "@/lib/processingDocumentsStorage";
import {
  knowledgeLinksQueryKey,
  linkScanQueryKey,
  type LinkScanUpdate,
} from "@/services/knowledgebase/KnowledgeLinksService";

export interface DocumentRealtimePayload {
  type: "document_update";
  document_id: string;
  status?: "completed" | "failed";
  mode?: "scrape" | "research";
  process_type?: "extraction_started" | "extraction_completed" | "optimization_started" | "optimization_completed" | "consolidation_completed" | "consolidation_failed" | "kb_added";
  handler?: string;
  el_doc_id?: string;
  timestamp: string;
  progress?: number;
  [key: string]: unknown;
}

interface AgentKbUpdatedPayload {
  action?: "add" | "attach" | "remove" | "detach" | "replace";
  agent_id?: string;
  document_id?: string;
  documents?: Array<{ id: string }>;
  syncedAgents?: number;
  attachedCount?: number;
  detachedCount?: number;
  replacedCount?: number;
}

interface UseDocumentRealtimeOptions {
  onDocumentUpdate?: (payload: DocumentRealtimePayload) => void;
  showToast?: boolean;
  userId?: string;
  invalidateAgentQueries?: boolean;
}

export const useDocumentRealtime = (options: UseDocumentRealtimeOptions = {}) => {
  const { userId } = options;
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const optionsRef = useRef(options);
  const hubRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  optionsRef.current = options;

  // A knowledge change kicks the server-side page regeneration (auto-provision),
  // which takes a little while to write the refreshed pack. Refetch the public
  // page now for the change itself, and once more after the regeneration has
  // had time to land, so an open Genie Page follows its knowledge base.
  const invalidateHubQueries = useCallback(() => {
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ["public-help-hub"] });
      queryClient.invalidateQueries({ queryKey: ["public-help-guide"] });
    };
    invalidate();
    if (hubRefreshTimerRef.current) clearTimeout(hubRefreshTimerRef.current);
    hubRefreshTimerRef.current = setTimeout(invalidate, 90 * 1000);
  }, [queryClient]);

  useEffect(
    () => () => {
      if (hubRefreshTimerRef.current) clearTimeout(hubRefreshTimerRef.current);
    },
    [],
  );

  const isUserDocument = useCallback(
    (documentId: string): boolean => {
      if (!userId) return false;

      const queries = queryClient.getQueriesData({ queryKey: ["documents"] });

      for (const [_, data] of queries) {
        if (Array.isArray(data)) {
          const doc = data.find(
            (d: { id?: string; el_doc_id?: string; user_id?: string }) =>
              d.id === documentId || d.el_doc_id === documentId,
          );
          if (doc) {
            return doc.user_id === userId;
          }
        }
      }

      return false;
    },
    [userId, queryClient],
  );

  useEffect(() => {
    if (channelRef.current) {
      return;
    }

    const channel = supabase.channel("document-updates");

    channel
      .on("broadcast", { event: "document_status_changed" }, ({ payload }: { payload: DocumentRealtimePayload }) => {
        console.log("Received document realtime update:", payload);

        // Update global parsing progress state
        if (payload.process_type === 'extraction_started' && payload.progress !== undefined) {
          updateDocumentParsingProgress(payload.document_id, payload.progress);
        } else if (payload.process_type === 'extraction_completed' || payload.process_type === 'optimization_completed' || payload.process_type === 'kb_added') {
          updateDocumentParsingProgress(payload.document_id, 100);
        }

        const opts = optionsRef.current;
        const shouldShowToast = opts.showToast && (!opts.userId || isUserDocument(payload.document_id));

        const processType = payload.process_type || payload.mode;
        const isCompletionEvent = payload.status === "completed" || payload.status === "failed" || processType?.includes("completed");

        if (isCompletionEvent) {
          const allDocs = processingDocumentsStorage.getAll();
          const docToRemove = allDocs.find((d) => d.documentId === payload.document_id);
          if (docToRemove) {
            processingDocumentsStorage.remove(payload.document_id, docToRemove.agentId);
            window.dispatchEvent(new Event("processing-documents-changed"));
          }
        }

        if (shouldShowToast) {
          if (payload.status === "completed" || processType?.includes("completed")) {
            if (processType === "extraction_completed") {
              toast.success("Document extracted successfully", {
                description: "Your document is now being optimized.",
                duration: 5000,
              });
            } else if (processType === "optimization_completed") {
              toast.success("Document optimized successfully", {
                description: "Your document will be added to the knowledge base shortly.",
                duration: 5000,
              });
            } else if (processType === "scrape") {
              toast.success("Website scraped successfully", {
                description: "Your document is now being optimized.",
                duration: 5000,
              });
            } else if (processType === "research") {
              toast.success("Research completed successfully", {
                description: "Your research document has been added to the knowledge base.",
                duration: 5000,
              });
            } else if (processType === "consolidation_completed") {
              toast.success("Documents consolidated successfully", {
                description: `${payload.source_documents || "Your"} document${payload.source_documents !== 1 ? "s" : ""} have been combined into ${payload.parts || 1} file${payload.parts !== 1 ? "s" : ""}.`,
                duration: 5000,
              });
            } else if (processType === "kb_added") {
              toast.success("Added to Knowledge Base", {
                description: "Your document is now available to your Genies.",
                duration: 5000,
              });
            }
          } else if (payload.status === "failed" || processType?.includes("failed")) {
            if (processType === "extraction_completed") {
              toast.error("Document extraction failed", {
                description: (payload.error as string) || "There was an error extracting your document.",
                duration: 5000,
              });
            } else if (processType === "optimization_completed") {
              toast.error("Document optimization failed", {
                description: "There was an error optimizing your document.",
                duration: 5000,
              });
            } else if (processType === "scrape") {
              toast.error("Website scraping failed", {
                description: (payload.error as string) || "There was an error scraping the website.",
                duration: 5000,
              });
            } else if (processType === "research") {
              toast.error("Research failed", {
                description: "There was an error completing your research.",
                duration: 5000,
              });
            } else if (processType === "consolidation_failed") {
              toast.error("Unable to consolidate documents", {
                description: payload.reason || "The combined file size exceeds the knowledge base limit.",
                duration: 6000,
              });
            }
          }
        }

        // For progress updates (extraction_started), refetch immediately
        // For completion/failure events, also refetch documents
        if (payload.process_type === 'extraction_started' || payload.process_type === 'optimization_started') {
          queryClient.refetchQueries({ queryKey: ["documentParsingStatus"], type: "active" });
        } else if (isCompletionEvent) {
          queryClient.invalidateQueries({ queryKey: ["documents"] });
          queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
          queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
          queryClient.invalidateQueries({ queryKey: ["documentParsingStatus"] });
          invalidateHubQueries();
        }

        // For intermediate events (optimize completed, consolidation completed, kb_added), also invalidate documents so drawer gets updated data
        if (payload.process_type === 'optimization_completed' || payload.process_type === 'consolidation_completed' || payload.process_type === 'kb_added' || payload.handler === 'ElevenLabs') {
          queryClient.invalidateQueries({ queryKey: ["documents"] });
          queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
          queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
        }

        // Notify parent of updates so they can respond accordingly (e.g., drawer refresh)
        opts.onDocumentUpdate?.(payload);
      })
      .on("broadcast", { event: "agent_kb_updated" }, ({ payload }: { payload: AgentKbUpdatedPayload }) => {
        console.log("Received agent KB update:", payload);

        const opts = optionsRef.current;

        // Clear processing documents when attached to KB
        if (payload.action === "attach" && payload.documents && Array.isArray(payload.documents)) {
          payload.documents.forEach((doc) => {
            processingDocumentsStorage.remove(doc.id, payload.agent_id);
          });
          window.dispatchEvent(new Event("processing-documents-changed"));
        }

        queryClient.invalidateQueries({ queryKey: ["documents"] });
        queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
        queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
        queryClient.invalidateQueries({ queryKey: ["agent"] });
        queryClient.invalidateQueries({ queryKey: ["agents"] });
        queryClient.invalidateQueries({ queryKey: ["user-agents"] });
        queryClient.invalidateQueries({ queryKey: ["all-agents"] });
        invalidateHubQueries();

        if (opts.showToast && (!opts.userId || isUserDocument(payload.document_id) || isUserDocument(payload.agent_id))) {
          if (payload.action === "add" || payload.action === "attach") {
            toast.success("Document associated with Genie", {
              description: `${payload.syncedAgents || payload.attachedCount || 1} document${payload.syncedAgents !== 1 ? "s" : ""} attached to Genie.`,
              duration: 4000,
            });
          } else if (payload.action === "remove" || payload.action === "detach") {
            toast.info("Document removed from Knowledge Base", {
              description: `${payload.syncedAgents || payload.detachedCount || 1} document${payload.syncedAgents !== 1 ? "s" : ""} removed from Genie.`,
              duration: 4000,
            });
          } else if (payload.action === "replace") {
            toast.success("Knowledge Base updated", {
              description: `Genie knowledge base replaced with ${payload.replacedCount || payload.syncedAgents || 1} document${payload.replacedCount !== 1 ? "s" : ""}.`,
              duration: 4000,
            });
          }
        }
      })
      .on("broadcast", { event: "link_scan_updated" }, ({ payload }: { payload: LinkScanUpdate }) => {
        const opts = optionsRef.current;
        if (opts.userId && payload.user_id !== opts.userId) return;

        queryClient.setQueryData(linkScanQueryKey(payload.agent_id), payload);
        if (payload.status !== "completed") return;

        queryClient.invalidateQueries({ queryKey: knowledgeLinksQueryKey(payload.agent_id) });
        if (!opts.showToast) return;

        const documents = `${payload.total} document${payload.total === 1 ? "" : "s"}`;
        if (payload.found === 0) {
          toast.info("No links found in the knowledge base", { description: `Read ${documents}.`, duration: 6000 });
          return;
        }
        toast.success(`Found ${payload.found} link${payload.found === 1 ? "" : "s"} in the knowledge base`, {
          description: `Read ${documents}. Pick the ones to show on the Resources card.`,
          duration: 10000,
          action: {
            label: "Review",
            onClick: () => window.location.assign(`/genie-hub/my-genies/${payload.agent_id}?section=section-resources`),
          },
        });
      })
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          console.log("Subscribed to document updates channel");
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
        console.log("Unsubscribed from document updates channel");
      }
    };
    // Mount-once channel; handlers read live options through optionsRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
